/* Custom-size ordering on catalog rug pages.
 *
 * The estimate shown here mirrors the backend's ladder pricing exactly:
 * the price is interpolated between the two LISTED sizes bracketing the
 * area, so it always lands between its neighbours' prices, never shrinks
 * as the rug grows, and an exact listed size costs exactly its listed
 * price. The swatch (12"x12") anchors the ladder at 1 sqft — the 1'x1'
 * floor prices like the swatch. The charged price is always recomputed
 * server-side from the same ladder — this script only displays; the
 * add-to-cart request carries dimensions, never a price.
 */
(function () {
  'use strict';

  // One side of a size option, in any form the catalog actually uses:
  //   8'      8 ft            6'6"   6.5 ft (mixed feet+inches)
  //   12"     1 ft (swatch)   14     bare number = feet (missing foot mark)
  // Parsing per-dimension rather than matching the whole string keeps mixed
  // titles like "4' x 6'6"" and "2'6" x 10'" in the ladder. Dropping them
  // silently removed real anchors, which made a custom size at exactly that
  // listed size quote — and charge — LESS than the listed variant.
  var DIM_RE = /^\s*(\d+(?:\.\d+)?)\s*(?:(["”″])|['′’]\s*(?:(\d+(?:\.\d+)?)\s*["”″]?)?)?\s*$/;

  function dimToFeet(token) {
    var m = DIM_RE.exec(token);
    if (!m) return null;
    var n = parseFloat(m[1]);
    if (isNaN(n)) return null;
    if (m[2]) return n / 12;                       // pure inches
    return n + (m[3] ? parseFloat(m[3]) / 12 : 0); // feet, optional inches
  }

  function ladderDims(title) {
    var parts = String(title).replace(/^\s*swatch\s+/i, '').split(/[x×]/i);
    if (parts.length !== 2) return null;
    var w = dimToFeet(parts[0]);
    var l = dimToFeet(parts[1]);
    if (w === null || l === null || !(w > 0) || !(l > 0)) return null;
    return [w, l];
  }

  /* A round product lists its DIAMETER twice ("4' x 4'"), so on a round request
   * a SQUARE title is read as a diameter — the same circle the quote measures.
   * Reading it as 16 sqft while quoting pi*2^2 = 12.57 made a round custom size
   * price BELOW its own listed round size. Mirrors pricing._ladder_sqft. */
  function isSquareTitle(widthFt, lengthFt) {
    return Math.abs(widthFt - lengthFt) < 0.01;
  }

  function ladderSqft(title, shape) {
    var d = ladderDims(title);
    if (!d) return null;
    if (shape === 'round' && isSquareTitle(d[0], d[1])) {
      return Math.PI * Math.pow(d[0] / 2, 2);
    }
    return d[0] * d[1];
  }

  /* The listed variant whose DIMENSIONS match exactly, if there is one.
   * Area alone cannot price every product: 3'x5' and 2.5'x6' are both 15 sqft
   * but listed at different prices, and averaging them quoted LESS than the
   * listed 3'x5'. An exact dimension match always wins. Mirrors
   * pricing.exact_listed_price. */
  function exactListedPrice(variants, widthFt, lengthFt, shape) {
    var wantDiameter = Math.round(widthFt * 100) / 100;
    var want = [Math.round(widthFt * 100) / 100, Math.round(lengthFt * 100) / 100].sort(function (a, b) { return a - b; });
    for (var i = 0; i < (variants || []).length; i++) {
      var v = variants[i];
      var d = ladderDims(String(v.title || ''));
      if (!d) continue;
      if (shape === 'round') {
        if (!isSquareTitle(d[0], d[1]) || Math.round(d[0] * 100) / 100 !== wantDiameter) continue;
      } else {
        var have = [Math.round(d[0] * 100) / 100, Math.round(d[1] * 100) / 100].sort(function (a, b) { return a - b; });
        if (have[0] !== want[0] || have[1] !== want[1]) continue;
      }
      var price = (v.price || 0) / 100;
      if (!(price > 0)) continue;
      var compare = (v.compare_at_price || 0) / 100;
      return { price: roundToX99(price), compareAt: compare > price ? roundToX99(compare) : 0 };
    }
    return null;
  }

  function roundToX99(price) {
    price = Math.round(price * 1e6) / 1e6;
    var floored = Math.floor(price);
    var candidate = floored + 0.99;
    if (candidate < price) candidate = floored + 1.99;
    return Math.round(candidate * 100) / 100;
  }

  /* The 12"x12" swatch used to supply the 1 sqft anchor as a real variant. It
   * is being deleted from the catalog, so on a rug tagged swatch-removed the
   * anchor is passed in from Liquid instead. This MUST mirror
   * swatch_anchor_price_for + ladder_points_from_variants in
   * apps/ruggen/utils/pricing.py: the backend recomputes the charge from its
   * own ladder, so if this list disagrees the shopper is quoted one price and
   * billed another (measured: $225.99 shown vs $246.99 charged on a 2'x3').
   * Injected only when no 1 sqft point exists, so it is a no-op on rugs whose
   * swatch variant is still present. */
  function ladderFromVariants(variants, anchorPrice, shape) {
    var byArea = {};
    (variants || []).forEach(function (v) {
      var sqft = ladderSqft(String(v.title || ''), shape);
      var price = (v.price || 0) / 100;
      if (!(sqft > 0) || !(price > 0)) return;
      var compare = (v.compare_at_price || 0) / 100;
      (byArea[sqft] = byArea[sqft] || []).push({ price: price, compare: compare > price ? compare : 0 });
    });
    if (anchorPrice > 0 && !byArea[1]) byArea[1] = [{ price: anchorPrice, compare: 0 }];
    return Object.keys(byArea).map(function (key) {
      var rows = byArea[key];
      var compares = rows.filter(function (r) { return r.compare; });
      return {
        sqft: parseFloat(key),
        price: rows.reduce(function (s, r) { return s + r.price; }, 0) / rows.length,
        compare: compares.length
          ? compares.reduce(function (s, r) { return s + r.compare; }, 0) / compares.length
          : 0
      };
    }).sort(function (a, b) { return a.sqft - b.sqft; });
  }

  // Linear PRICE interpolation between the two listed sizes bracketing the
  // area: always between the neighbours' prices, never lower for a bigger
  // rug, exact at listed sizes. Outside the ladder the boundary size's
  // per-sqft rate extends the line. Mirrors pricing._bracket_price.
  function bracketPrice(anchors, sqft) {
    var first = anchors[0];
    var last = anchors[anchors.length - 1];
    if (sqft <= first.sqft) return (first.value / first.sqft) * sqft;
    if (sqft >= last.sqft) return (last.value / last.sqft) * sqft;
    for (var i = 0; i < anchors.length - 1; i++) {
      var lo = anchors[i], hi = anchors[i + 1];
      if (sqft >= lo.sqft && sqft <= hi.sqft) {
        return lo.value + (hi.value - lo.value) * (sqft - lo.sqft) / (hi.sqft - lo.sqft);
      }
    }
    return (last.value / last.sqft) * sqft;
  }

  function priceFor(ladder, sqft) {
    var raw = bracketPrice(ladder.map(function (p) { return { sqft: p.sqft, value: p.price }; }), sqft);
    var price = roundToX99(raw);
    var compareAnchors = ladder.filter(function (p) { return p.compare; })
      .map(function (p) { return { sqft: p.sqft, value: p.compare }; });
    var compareAt = 0;
    if (compareAnchors.length >= 2) {
      compareAt = roundToX99(bracketPrice(compareAnchors, sqft));
      if (compareAt <= price) compareAt = 0;
    }
    return { price: price, compareAt: compareAt, rate: raw / sqft };
  }

  /* The quote composition the backend charges with: an exact dimension match
   * wins over the area ladder — including round, whose listed diameters ARE
   * reachable once they are read as diameters. Shared by the custom panel and
   * the pad row, so the two can never price the same size differently. */
  function quoteFor(variants, ladder, widthFt, lengthFt, shape) {
    var exact = exactListedPrice(variants, widthFt, lengthFt, shape);
    if (exact) return exact;
    var sqft = shape === 'round' ? Math.PI * Math.pow(widthFt / 2, 2) : widthFt * lengthFt;
    return priceFor(ladder, sqft);
  }

  /* The smallest rug the workshop takes to order, mirrored from
   * MIN_CUSTOM_SHORT_FT / MIN_CUSTOM_LONG_FT in apps/ruggen/utils/pricing.py.
   * Orientation is irrelevant, so these bound the SHORT and LONG side rather
   * than width and length — 3'x2' is the same rug as 2'x3'. Round and square
   * rugs have one dimension, which therefore has to clear the longer floor.
   * The two gates must agree, or this quotes a size the API then refuses. */
  var MIN_SHORT_FT = 2;
  var MIN_LONG_FT = 3;

  /* ---------------- matching rug pad: pure logic (task 095) -----------------
   * Copied from Maia's live assets/rug-size-options.js (task 085/090) so both
   * stores decide the same way, and unit-tested against this very file
   * (.claude/tools/pad-addon-tests-1k). Sizes are read with ladderDims — the
   * parser that mirrors pricing._ladder_dims, never a second one — and
   * compared in WHOLE INCHES, the unit the custom-size endpoint takes. */

  // Owner question pending (UX spec §12 Q3): the largest pad we can cut.
  var PAD_MAX_SHORT_FT = 15;
  var PAD_MAX_LONG_FT = 20;
  // How long a pad mint may hold the rug back: at least PAD_MIN_WAIT_MS from
  // the click (a first-time mint is several Admin API calls) and at least
  // PAD_GRACE_MS after the rug's own variant is ready; the later one wins.
  var PAD_MIN_WAIT_MS = 8000;
  var PAD_GRACE_MS = 3000;

  var PAD_SHAPE_FOR_ROLE = { rect: 'rectangular', square: 'square', round: 'round' };

  function padRoleForShape(shape) {
    if (shape === 'rectangular' || shape === 'runner') return 'rect';
    if (shape === 'square' || shape === 'round') return shape;
    return null;
  }

  function toInches(feet) { return Math.round(feet * 12); }

  // Mirrors views._dim_label: the label a custom line carries in the cart.
  function dimLabel(inches) {
    var ft = Math.floor(inches / 12);
    var rest = inches % 12;
    return ft + "'" + (rest ? rest + '"' : '');
  }

  // Mirrors the size_label CustomSizeCheckoutView writes into a one-off's
  // title, so the pad row reads exactly like the custom line it will add.
  function customSizeLabel(shape, widthIn, lengthIn) {
    if (shape === 'round') return dimLabel(widthIn) + ' round';
    return dimLabel(widthIn) + ' × ' + dimLabel(lengthIn);
  }

  function listedSizeLabel(sizeValue) {
    return String(sizeValue || '').replace(/X/g, 'x').trim();
  }

  function sortedInches(dims) {
    return [toInches(dims[0]), toInches(dims[1])].sort(function (a, b) { return a - b; });
  }

  // want: the rug's [short, long] side in whole inches.
  function findListedPad(padVariants, want) {
    for (var i = 0; i < (padVariants || []).length; i++) {
      var d = ladderDims(String(padVariants[i].title || ''));
      if (!d) continue;
      var have = sortedInches(d);
      if (have[0] === want[0] && have[1] === want[1]) return padVariants[i];
    }
    return null;
  }

  /* The rug the pad must match, from a listed variant. null = no row: sold
   * out, a swatch variant, or a size the parser cannot read. */
  function listedRug(variant, sizeIndex, role) {
    if (!variant || !variant.available) return null;
    var value = sizeIndex >= 0 && variant.options
      ? variant.options[sizeIndex]
      : String(variant.title || '').split('/')[0];
    if (/^\s*swatch\b/i.test(value)) return null;
    var dims = ladderDims(String(value));
    if (!dims) return null;
    return { mode: 'listed', role: role, dims: dims, sizeLabel: listedSizeLabel(value) };
  }

  // The endpoint's minimums (serializer.validate) and the pad cap. A round or
  // square pad has one side, so it must clear the LONG minimum.
  function padSizeProblem(shortIn, longIn) {
    if (shortIn < MIN_SHORT_FT * 12 || longIn < MIN_LONG_FT * 12) return 'small';
    if (shortIn > PAD_MAX_SHORT_FT * 12 || longIn > PAD_MAX_LONG_FT * 12) return 'large';
    return null;
  }

  /* What the row offers for the rug as configured right now.
   *   rug:  listedRug(...) or { mode: 'custom', shape, dims: [w, l] ft, invalid }
   *   pads: { rect | square | round: { id, url, variants: [{id, title, price, available}] } }
   * -> { status: hidden | invalid | unavailable | listed | custom, ... }
   * A listed pad of the same size is reused (if it is in stock); anything
   * else is minted at the rug's own dimensions and priced with the same
   * quote the backend will charge. A Trade (B2B) buyer's Liquid prices are
   * its catalog's, so it gets a trade quote, as the panel does for the rug
   * (the cart discounts the retail mint, so cents can differ). */
  function planPad(rug, pads) {
    if (!rug) return { status: 'hidden' };
    var role = rug.mode === 'custom' ? padRoleForShape(rug.shape) : rug.role;
    var shape = rug.mode === 'custom' ? rug.shape : PAD_SHAPE_FOR_ROLE[role];
    // Still names its pad, so the thumbnail keeps the panel's shape.
    if (rug.invalid) return { status: 'invalid', shape: shape, pad: (pads && pads[role]) || null };
    var widthIn = toInches(rug.dims[0]);
    var lengthIn = toInches(rug.dims[1]);
    var sides = sortedInches(rug.dims);
    // A round size reads "8' round", listed or custom (UX §3). A listed
    // round size that is not square keeps its own title: it gets no pad.
    var labelled = rug.mode === 'custom' || (shape === 'round' && sides[0] === sides[1]);
    var plan = {
      status: 'unavailable',
      shape: shape,
      pad: (pads && pads[role]) || null,
      size: labelled ? customSizeLabel(shape, widthIn, lengthIn) : rug.sizeLabel
    };
    if (!plan.pad) return plan;
    var oneSided = role !== 'rect';
    if (oneSided && sides[0] !== sides[1]) return plan;
    var variant = findListedPad(plan.pad.variants, sides);
    if (variant) {
      if (!variant.available) return plan;
      plan.status = 'listed';
      plan.variant = variant;
      plan.price = variant.price / 100;   // Liquid variant JSON is CENTS
      return plan;
    }
    if (padSizeProblem(sides[0], sides[1])) return plan;
    var ladder = ladderFromVariants(plan.pad.variants, 0, shape === 'round' ? 'round' : 'rectangular');
    plan.status = 'custom';
    plan.price = quoteFor(plan.pad.variants, ladder, widthIn / 12, lengthIn / 12, shape).price;
    plan.request = {
      shape: shape,
      width_ft: Math.floor(widthIn / 12),
      width_in: widthIn % 12,
      length_ft: oneSided ? 0 : Math.floor(lengthIn / 12),
      length_in: oneSided ? 0 : lengthIn % 12
    };
    return plan;
  }

  function padPriceText(priceText, qty, eachWord) {
    return qty >= 2 && eachWord ? priceText + ' ' + eachWord : priceText;
  }

  /* "+ pad" replaces only the theme's own default label (which may carry the
   * price suffix) and the custom-size label; any other label — a blocked
   * reason, "Sold out", "Pre-order" — wins. */
  function padAtcLabel(label, mode, ctx) {
    if (mode === 'custom') return label === ctx.customLabel ? ctx.customPadLabel : label;
    var normal = String(label || '').replace(/\s+/g, ' ').trim();
    return ctx.defaultLabel && normal.indexOf(ctx.defaultLabel) === 0 ? ctx.listedPadLabel : label;
  }

  /* The rug waits for its pad until minWaitMs after the call (the click) or
   * graceMs after its own variant is ready, whichever is later; a later pad
   * is dropped. Only a RUG failure rejects. */
  function waitForPad(rugReady, padPending, minWaitMs, graceMs) {
    var started = Date.now();
    if (padPending) padPending.catch(function () {});
    return rugReady.then(function (rug) {
      if (!padPending) return { rug: rug, pad: null, padFailed: false };
      var wait = Math.max(started + minWaitMs, Date.now() + graceMs) - Date.now();
      return new Promise(function (resolve) {
        var timer = setTimeout(function () {
          resolve({ rug: rug, pad: null, padFailed: true });
        }, wait);
        padPending.then(function (pad) {
          clearTimeout(timer);
          resolve({ rug: rug, pad: pad || null, padFailed: !pad });
        }, function () {
          clearTimeout(timer);
          resolve({ rug: rug, pad: null, padFailed: true });
        });
      });
    });
  }

  /* One request with the pad as a child; if that is refused, the rug alone,
   * once. Never a pad without its rug. */
  function addRugWithPad(add, rug, pad) {
    if (!pad) return add(rug, null).then(function () { return { padAdded: false, padFailed: false }; });
    return add(rug, pad).then(function () {
      return { padAdded: true, padFailed: false };
    }, function () {
      return add(rug, null).then(function () { return { padAdded: false, padFailed: true }; });
    });
  }

  // The Shopify Ajax API's items[n] encoding (Eurus 9's product-cart.js uses
  // it for add-ons; 8.1 has none), so a pad child is a nested cart line.
  function childItemEntries(index, item) {
    var base = 'items[' + index + ']';
    var out = [[base + '[id]', String(item.id)], [base + '[quantity]', String(item.quantity)]];
    if (item.parent_id) out.push([base + '[parent_id]', String(item.parent_id)]);
    Object.keys(item.properties || {}).forEach(function (key) {
      out.push([base + '[properties][' + key + ']', item.properties[key]]);
    });
    return out;
  }

  function padProperties(rugProperties, noteKey) {
    var out = { _pad_source: 'rug-pdp' };
    if (noteKey && rugProperties && rugProperties[noteKey]) out[noteKey] = rugProperties[noteKey];
    return out;
  }

  // pad.url is Liquid's product.url: a bare path, never a query string.
  function padShopUrl(plan) {
    return plan.status === 'listed' ? plan.pad.url + '?variant=' + plan.variant.id : plan.pad.url;
  }

  // The matched listed variant's own image, else the pad's featured image.
  function padImage(plan) {
    if (!plan.pad) return null;
    return (plan.status === 'listed' && plan.variant.image) || plan.pad.image || null;
  }

  function padAttachEvent(plan, checked, added, rugMode, productId) {
    if (!plan || (plan.status !== 'listed' && plan.status !== 'custom')) return null;
    return {
      pad_checked: !!checked,
      pad_added: !!added,
      pad_kind: plan.status,
      shape: plan.shape,
      rug_mode: rugMode,
      pad_price: plan.price,
      product_id: productId
    };
  }

  /* ---------------- end pad pure logic -------------------------------------- */

  /* ---------------- theme cart contract ------------------------------------
   * Eurus's own cart-add contract — the same one assets/product-cart.js uses.
   * The shopper stays on the page and the drawer opens over it with the
   * merchant's upsells. Deliberately NOT the cart permalink: a permalink
   * navigates to the .myshopify.com domain AND replaces the cart, throwing
   * away whatever the shopper had already added. */
  function themeCartAvailable() {
    return !!(window.Alpine && window.Alpine.store && window.Alpine.store('xCartHelper') &&
      window.Eurus && window.Eurus.cart_add_url && typeof getSectionInnerHTML === 'function');
  }

  // item: { id, quantity, properties, attributes } — attributes are the cart
  // attributes the theme's own product form posts alongside the line.
  function lineFormData(item, child) {
    var formData = new FormData();
    formData.append('id', String(item.id));
    formData.append('quantity', String(item.quantity));
    Object.keys(item.properties).forEach(function (key) {
      formData.append('properties[' + key + ']', item.properties[key]);
    });
    Object.keys(item.attributes).forEach(function (key) {
      formData.append('attributes[' + key + ']', item.attributes[key]);
    });
    // A pad rides in the same request as a child line of the rug (parent_id).
    if (child) childItemEntries(0, child).forEach(function (pair) { formData.append(pair[0], pair[1]); });
    return formData;
  }

  // Shopify said no (422): nothing was added, and re-posting cannot help.
  function refusal(body) {
    var err = new Error(body.description || body.message || 'Add to cart failed');
    err.refused = true;
    return err;
  }

  function postToThemeCart(item, child, sections) {
    var formData = lineFormData(item, child);
    formData.append('sections', sections.map(function (s) { return s.id; }));
    formData.append('sections_url', window.location.pathname);
    return fetch(window.Eurus.cart_add_url, {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData
    }).then(function (r) { return r.json(); }).then(function (response) {
      if (String(response.status) === '422') throw refusal(response);
      return response;
    });
  }

  function renderThemeCart(sections, response) {
    sections.forEach(function (section) {
      var el = document.querySelector(section.selector);
      if (el && response.sections && response.sections[section.id]) {
        el.innerHTML = getSectionInnerHTML(response.sections[section.id], section.selector);
      }
    });
    if (window.Alpine.store('xMiniCart')) window.Alpine.store('xMiniCart').openCart();
    var bubble = document.querySelector('#cart-icon-bubble span');
    if (bubble) window.Alpine.store('xCartHelper').currentItemCount = parseInt(bubble.innerHTML, 10);
    document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));
  }

  /* Fallback when the drawer isn't available: add through the standard cart
   * API and then show the cart. Never the /cart/<variant>:<qty> permalink —
   * that both leaves the storefront domain for *.myshopify.com and REPLACES
   * the cart, silently discarding anything the shopper had already added. */
  function addThenGoToCart(item, child) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: lineFormData(item, child)
    }).then(function (r) { return r.json(); }).then(function (body) {
      if (body && body.status && String(body.status) !== '200') throw refusal(body);
      window.location.href = '/cart';
    });
  }

  /* The /cart/add.js fallback is ONLY for a request that never got a usable
   * answer (network failure, unparseable body). A refusal is final, and once
   * Shopify has accepted the line, a drawer that fails to render must never
   * re-post it — the old catch-all fallback here added the rug twice. */
  function addToCart(item, child) {
    if (!themeCartAvailable()) return addThenGoToCart(item, child);
    var sections = window.Alpine.store('xCartHelper').getSectionsToRender();
    return postToThemeCart(item, child, sections).then(function (response) {
      try {
        renderThemeCart(sections, response);
      } catch (e) {
        if (window.console) console.error('custom-size-rug: cart drawer render failed', e);
      }
      return response;
    }, function (err) {
      if (err && err.refused) throw err;
      return addThenGoToCart(item, child);
    });
  }

  /* ---------- "Custom size" option inside the Size picker ----------
   * The shopper is already choosing a size in that row, so the entry point
   * belongs there rather than as a separate link underneath. The button clones
   * the classes off a real size option so it matches the theme exactly (and
   * keeps matching if the merchant restyles the picker). The theme re-renders
   * the picker on every variant change, which destroys the injected node, so
   * a MutationObserver puts it back.
   */
  function sizeOptionLabels() {
    var radios = Array.prototype.slice.call(document.querySelectorAll('input[type="radio"]'))
      .filter(function (r) { return /^size(-\d+)?\s*$/i.test(r.name || ''); });
    return radios.map(function (r) {
      return document.querySelector('label[for="' + (window.CSS && CSS.escape ? CSS.escape(r.id) : r.id) + '"]');
    }).filter(function (l) { return l && l.offsetParent !== null; });
  }

  function injectSizeButton(root, config, onToggle) {
    var labels = sizeOptionLabels();
    if (!labels.length) return false;
    var container = labels[0].parentElement;
    if (!container || container.querySelector('[data-cs-size-btn]')) return true;

    // CLONE a real option rather than building a <button>. Same element type,
    // same classes, same inner markup — so it cannot drift from the theme.
    // A <button> is not interchangeable here: this theme styles buttons as
    // 100px pills with appearance:button and centred text, so a button-based
    // option renders as a pill among square size chips.
    // Clone an UNSELECTED option, so the resting appearance is the resting one
    // (labels[0] may be the currently checked size).
    var ref = labels.filter(function (l) {
      var input = l.htmlFor ? document.getElementById(l.htmlFor) : null;
      return input && !input.checked;
    })[0] || labels[0];
    var opt = ref.cloneNode(true);
    opt.removeAttribute('for');
    opt.removeAttribute('id');
    // A clone can carry the source option's own controls/markup; keep only a
    // single span so the label text is all that differs.
    var refSpan = ref.querySelector('span');
    opt.innerHTML = '';
    var span = document.createElement('span');
    if (refSpan && refSpan.className) span.className = refSpan.className;
    span.textContent = config.sizeButtonLabel || 'Custom size';
    opt.appendChild(span);

    opt.classList.add('cs-rug__size-btn');
    opt.setAttribute('data-cs-size-btn', '');
    // role=button is needed for assistive tech, but this theme also STYLES
    // [role="button"] as a 100px pill with light text, which would make this
    // option a pill among square size chips. Pin the two affected properties
    // to whatever a real option computes to, so it tracks the theme rather
    // than hardcoding a look. The selected state overrides these with
    // !important in the stylesheet.
    var refStyle = getComputedStyle(ref);
    opt.style.borderRadius = refStyle.borderRadius;
    opt.style.color = refStyle.color;
    opt.setAttribute('role', 'button');
    opt.setAttribute('tabindex', '0');
    opt.setAttribute('aria-expanded', String(!root.querySelector('[data-cs-panel]').hidden));
    var panelId = root.querySelector('[data-cs-panel]').id;
    if (panelId) opt.setAttribute('aria-controls', panelId);

    // A re-render drops the class with the old fieldset; restore it so an open
    // panel keeps the standard option suppressed.
    container.classList.toggle('cs-rug-custom-active', !root.querySelector('[data-cs-panel]').hidden);

    opt.addEventListener('click', onToggle);
    opt.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        onToggle();
      }
    });
    container.appendChild(opt);
    return true;
  }

  /* While the custom panel is open the shopper's size IS the custom one, so the
   * standard option must not keep looking selected too. The radio is left
   * checked (the theme's variant state depends on it) — only its selected
   * styling is neutralised, using values read from a real unselected sibling so
   * this stays correct whatever palette the merchant sets. */
  /* Custom size and Swatch are both injected into the Size picker, and each
   * used to track its own state, so a shopper could have BOTH reading as
   * selected. They are alternatives — only one option in that row can be the
   * chosen one. Each announces its selection and clears itself when it hears
   * another, so neither file has to reach into the other's internals, and a
   * third injected option would drop in the same way. */
  var SIZE_OPTION_EVENT = 'eurus:size-option:selected';


  /* Clicking the size that is ALREADY checked fires NO change event, so a
   * change listener alone leaves the injected option selected — landing on
   * ?variant=<12'x15'> and then clicking 12' x 15' read as a dead option.
   * A click on a real size option means "I want that size" whether or not the
   * radio state actually moves. */
  function isRealSizeOptionClick(e) {
    var el = e.target;
    if (!el || !el.closest) return false;
    if (el.closest('[data-sw-option], [data-cs-size-btn]')) return false;
    var radio = null;
    if (el.matches && el.matches('input[type="radio"]')) {
      radio = el;
    } else {
      var label = el.closest('label[for]');
      if (label) radio = document.getElementById(label.htmlFor);
    }
    return !!(radio && /^size(-\d+)?\s*$/i.test(radio.name || ''));
  }

  function readJson(root, selector) {
    var el = root.querySelector(selector);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function formatMoney(amount, currency) {
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(amount);
    } catch (e) {
      return '$' + amount.toFixed(2);
    }
  }

  function initWidget(root) {
    var config = readJson(root, '[data-cs-config]') || {};
    var rawVariants = readJson(root, '[data-cs-ladder]') || [];
    // One ladder per geometry — the round reading of the same variants differs
    // and shape is chosen at runtime. Memoised; this runs on every keystroke.
    var ladderCache = {};
    function ladderFor(shapeName) {
      var key = shapeName === 'round' ? 'round' : 'rectangular';
      if (!ladderCache[key]) {
        ladderCache[key] = ladderFromVariants(rawVariants, config.swatchAnchorPrice, key);
      }
      return ladderCache[key];
    }
    var ladder = ladderFor('rectangular');

    var el = {
      panel: root.querySelector('[data-cs-panel]'),
      shape: root.querySelector('[data-cs-shape]'),
      shapeRow: root.querySelector('[data-cs-shape-row]'),
      widthLabel: root.querySelector('[data-cs-width-label]'),
      widthFt: root.querySelector('[data-cs-width-ft]'),
      widthIn: root.querySelector('[data-cs-width-in]'),
      lengthRow: root.querySelector('[data-cs-length-row]'),
      lengthFt: root.querySelector('[data-cs-length-ft]'),
      lengthIn: root.querySelector('[data-cs-length-in]'),
      price: root.querySelector('[data-cs-price]'),
      compare: root.querySelector('[data-cs-compare]'),
      add: root.querySelector('[data-cs-add]'),
      error: root.querySelector('[data-cs-error]')
    };
    // customSize is false when Liquid renders only the pad row (a
    // no-custom-size rug); fewer than two ladder points gives no size curve to
    // price from.
    var customSizeUsable = config.customSize !== false && ladder.length >= 2 &&
      !!(el.panel && el.widthFt && el.add);

    /* ------------ matching rug pad (task 095) ------------
     * padWanted — what the shopper ticked — is the row's ONE piece of state.
     * Everything else (shown, disabled, size and price, the express buttons,
     * the add-button labels) is re-derived from it and the current selection
     * by refreshPad(), which the MutationObserver also runs: every DOM write
     * below is write-only-on-change. */
    var padCfg = readJson(root, '[data-cs-pad-config]');
    var padEl = padCfg && root.querySelector('[data-cs-pad]') ? {
      wrap: root.querySelector('[data-cs-pad]'),
      row: root.querySelector('[data-cs-pad-row]'),
      input: root.querySelector('[data-cs-pad-input]'),
      line: root.querySelector('[data-cs-pad-line]'),
      size: root.querySelector('[data-cs-pad-size]'),
      priced: root.querySelector('[data-cs-pad-priced]'),
      price: root.querySelector('[data-cs-pad-price]'),
      sub: root.querySelector('[data-cs-pad-sub]'),
      notice: root.querySelector('[data-cs-pad-notice]'),
      labels: Array.prototype.slice.call(root.querySelectorAll('[data-cs-pad-row] label')),
      thumb: root.querySelector('[data-cs-pad-thumb]'),
      img: root.querySelector('[data-cs-pad-img]'),
      details: root.querySelector('[data-cs-pad-details]'),
      variant: root.querySelector('[data-cs-pad-variant]')
    } : null;
    if (!customSizeUsable && !padEl) return; // nothing to offer — stay hidden

    root.hidden = false;
    if (el.shapeRow && el.shape && el.shape.options.length < 2) el.shapeRow.hidden = true;
    // The rug's own product section: both add buttons, the quantity and the
    // express buttons live in it.
    var scope = root.closest('.shopify-section') || document;

    function shape() { return el.shape ? el.shape.value : 'rectangular'; }
    function singleDimension() { return shape() === 'round' || shape() === 'square'; }

    function dims() {
      var width = parseInt(el.widthFt.value, 10) + parseInt(el.widthIn.value, 10) / 12;
      var length = singleDimension()
        ? width
        : parseInt(el.lengthFt.value, 10) + parseInt(el.lengthIn.value, 10) / 12;
      return { width: width, length: length };
    }

    function belowMinimum() {
      var d = dims();
      var sides = [d.width, d.length].sort(function (a, b) { return a - b; });
      return sides[0] < MIN_SHORT_FT || sides[1] < MIN_LONG_FT;
    }

    function render() {
      renderQuote();
      refreshPad();
    }

    function renderQuote() {
      if (el.lengthRow) el.lengthRow.hidden = singleDimension();
      if (el.widthLabel) {
        el.widthLabel.textContent = shape() === 'round' ? config.labelDiameter
          : shape() === 'square' ? config.labelSide : config.labelWidth;
      }
      if (belowMinimum()) {
        el.price.textContent = '';
        if (el.compare) el.compare.hidden = true;
        el.error.textContent = shape() === 'round' ? config.minRoundText : config.minSizeText;
        el.error.hidden = false;
        el.add.disabled = true;
        return;
      }
      el.add.disabled = false;
      // An exact dimension match wins over the area ladder — a custom size
      // that IS a listed size must show that listed price. Round is INCLUDED
      // since 2026-09-13: a round product lists its diameters as "4' x 4'", so
      // the listed size is reachable once read as a diameter.
      var d = dims();
      var quote = quoteFor(rawVariants, ladderFor(shape()), d.width, d.length, shape());
      el.price.textContent = formatMoney(quote.price, config.currency);
      if (el.compare) {
        el.compare.textContent = quote.compareAt ? formatMoney(quote.compareAt, config.currency) : '';
        el.compare.hidden = !quote.compareAt;
      }
      el.error.hidden = true;
    }

    function fail(message) {
      el.error.textContent = message || config.errorText;
      el.error.hidden = false;
      el.add.disabled = false;
      el.add.textContent = panelLabel();
    }

    function endpoint() {
      var path = 'custom-size/checkout/';
      if (config.proxyPath) return config.proxyPath.replace(/\/$/, '') + '/' + path;
      return (config.apiBaseUrl || '').replace(/\/$/, '') + '/api/ruggen/' + path;
    }

    // Analytics never breaks an add: a throwing dataLayer push (a broken tag
    // hook) is logged and dropped.
    function push(event, data) {
      try {
        if (window.dataLayer && window.dataLayer.push) {
          window.dataLayer.push(Object.assign({ event: event }, data || {}));
        }
      } catch (e) {
        if (window.console) console.error('custom-size-rug: analytics push failed', e);
      }
    }

    function track(name, data) { push('custom_size_' + name, data); }

    /* One custom-size mint, for the rug or its pad. A MOCK_VARIANT URL means
     * the backend has no store credentials — never send a live shopper to
     * that dead cart link. */
    function request(body) {
      return fetch(endpoint(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      }).then(function (r) {
        return r.json().catch(function () { return {}; }).then(function (payload) {
          if (!r.ok) throw new Error(payload.error || Object.values(payload || {})[0] || config.errorText);
          return payload;
        });
      }).then(function (payload) {
        if (payload.checkout_url && payload.checkout_url.indexOf('MOCK_VARIANT') > -1) {
          throw new Error(config.errorText);
        }
        if (!payload.variant_id) throw new Error(config.errorText);
        return payload;
      });
    }

    function setPanel(open) {
      if (el.panel.hidden === !open) return;   // already in that state
      el.panel.hidden = !open;
      root.classList.toggle('cs-rug--open', open);
      var btn = document.querySelector('[data-cs-size-btn]');
      if (btn) {
        btn.setAttribute('aria-expanded', String(open));
        // Suppresses the standard option's selected styling — see the
        // .cs-rug-custom-active rule in custom-size-rug.css.
        if (btn.parentElement) btn.parentElement.classList.toggle('cs-rug-custom-active', open);
      }
      if (open) {
        document.dispatchEvent(new CustomEvent(SIZE_OPTION_EVENT, {
          detail: { source: 'custom-size' }
        }));
        render();
        track('opened');
        el.panel.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      } else {
        refreshPad();
      }
    }

    function togglePanel() { setPanel(el.panel.hidden); }

    var padWanted = false;
    // What the row shows: the listed rug's pad, or the panel's while it is open.
    var padPlan = { status: 'hidden' };
    // The pad matching the LISTED rug — what the main and sticky buttons add.
    var listedPlan = { status: 'hidden' };
    // True while an add of ours is in flight; every add button is inert then.
    var ordering = false;
    // The rug_pad_attach event of a theme add (box unticked) still in flight,
    // with the rug button that started it: { btn, event } or null.
    var nativeAttach = null;

    function setText(node, text) {
      if (node && node.textContent !== text) node.textContent = text;
    }

    function panelOpen() { return customSizeUsable && !el.panel.hidden; }
    function swatchSelected() { return !!document.querySelector('[data-sw-option][aria-pressed="true"]'); }
    function enabled(plan) { return plan.status === 'listed' || plan.status === 'custom'; }
    function boxTicked() { return !!padEl && padWanted && enabled(padPlan); }
    // The main and sticky buttons add the LISTED rug, so they carry its pad.
    function padForButtons() { return boxTicked() && enabled(listedPlan) ? listedPlan : null; }
    function padForPanel() { return boxTicked() && panelOpen() ? padPlan : null; }

    function panelLabel() {
      return padForPanel() ? padCfg.text.atcCustom : config.buttonLabel;
    }

    function currentVariant() {
      var form = document.getElementById(padCfg.formId);
      var input = form && form.querySelector('input[name="id"]');
      if (!input) return null;
      for (var i = 0; i < rawVariants.length; i++) {
        if (String(rawVariants[i].id) === String(input.value)) return rawVariants[i];
      }
      return null;
    }

    function quantity() {
      var input = document.getElementById(padCfg.quantityId);
      var n = input ? parseInt(input.value, 10) : 1;
      return n > 0 ? n : 1;
    }

    function planFor(rug) { return planPad(rug, padCfg.pads); }

    function renderPad() {
      listedPlan = planFor(listedRug(currentVariant(), padCfg.sizeIndex, padCfg.role));
      if (swatchSelected()) {
        padPlan = { status: 'hidden' };
      } else if (panelOpen()) {
        var d = dims();
        padPlan = planFor({ mode: 'custom', shape: shape(), dims: [d.width, d.length], invalid: belowMinimum() });
      } else {
        padPlan = listedPlan;
      }
      var on = enabled(padPlan);
      // The panel adds ONE custom rug; the main buttons add the form's quantity.
      var qty = panelOpen() ? 1 : quantity();
      var priced = on ? padPriceText(formatMoney(padPlan.price, config.currency), qty, padCfg.text.each) : '';
      padEl.wrap.hidden = padPlan.status === 'hidden';
      padEl.input.disabled = !on || ordering;
      padEl.input.checked = padWanted && on;
      padEl.row.classList.toggle('opacity-40', !on);
      padEl.labels.forEach(function (label) { label.classList.toggle('cursor-pointer', on); });
      setText(padEl.size, padPlan.size || '');
      setText(padEl.price, priced);
      padEl.priced.hidden = !priced;
      // Announce size/price changes only while the pad is actually chosen,
      // so browsing sizes doesn't flood a screen reader.
      padEl.line.setAttribute('aria-live', padEl.input.checked ? 'polite' : 'off');
      setText(padEl.sub, padPlan.status === 'unavailable' ? padCfg.text.unavailable : padCfg.text.benefit);
      renderPadDetails();
    }

    /* The thumbnail and "View details" follow the pad the row would add (its
     * shape, and its listed size when there is one). The quick view reads the
     * variant to open on from the current-variant element, as on product
     * cards; empty = the pad's own default variant. */
    function renderPadDetails() {
      var src = padImage(padPlan);
      padEl.thumb.classList.toggle('cs-pad-hidden', !src);
      padEl.details.classList.toggle('cs-pad-hidden', !padPlan.pad);
      if (src && padEl.img.getAttribute('src') !== src) padEl.img.setAttribute('src', src);
      if (!padPlan.pad) return;
      var href = padShopUrl(padPlan);
      [padEl.thumb, padEl.details].forEach(function (link) {
        if (link.getAttribute('href') !== href) link.setAttribute('href', href);
      });
      setText(padEl.variant, padPlan.status === 'listed' ? String(padPlan.variant.id) : '');
    }

    /* Main + sticky: "+ pad" over the theme's own label. Anything that is not
     * our own last write is the THEME's label — a variant change re-renders it
     * ("Sold out", "Pre-order") — and that fresh label is the one to restore. */
    function rugButtons() {
      if (!padEl) return [];
      return [padCfg.formId, padCfg.stickyFormId].map(function (id) {
        var form = document.getElementById(id);
        return form && form.querySelector('button[name="add"]');
      }).filter(Boolean);
    }

    function isRugButton(btn) {
      var id = btn.form && btn.form.getAttribute('id');
      return !!id && (id === padCfg.formId || id === padCfg.stickyFormId);
    }

    function syncLabels() {
      var withPad = !!padForButtons();
      var ctx = { defaultLabel: padCfg.atcDefaultLabel, listedPadLabel: padCfg.text.atcListed };
      rugButtons().forEach(function (btn) {
        var span = btn.querySelector('.x-atc-text');
        if (!span) return;
        var shown = span.textContent;
        if (!btn.hasAttribute('data-cs-original') || shown !== btn.getAttribute('data-cs-written')) {
          btn.setAttribute('data-cs-original', shown);
        }
        var label = btn.getAttribute('data-cs-original');
        if (withPad) label = padAtcLabel(label, 'listed', ctx);
        setText(span, label);
        btn.setAttribute('data-cs-written', label);
      });
      // The panel's own button, only while it shows a resting label (never
      // over its busy or added label).
      if (customSizeUsable && (el.add.textContent === config.buttonLabel ||
          el.add.textContent === padCfg.text.atcCustom)) {
        setText(el.add, panelLabel());
      }
    }

    /* Dynamic checkout (Buy It Now) goes straight to a wallet sheet and
     * structurally cannot carry a second line: with the pad ticked it would
     * buy the rug without its pad. Collapsed, not intercepted; `inert` takes
     * it out of the tab order too. */
    function syncExpress() {
      var hide = boxTicked();
      Array.prototype.forEach.call(scope.querySelectorAll('.payment-button-container'), function (node) {
        node.classList.toggle('cs-pad-express-hidden', hide);
        if (node.hasAttribute('inert') !== hide) node.toggleAttribute('inert', hide);
      });
    }

    function refreshPad() {
      if (!padEl) return;
      renderPad();
      syncLabels();
      syncExpress();
    }

    // The theme's own quick view (quick-view.js), driven the way product cards
    // drive it: load() on hover/focus, open() on click. Off or absent, the
    // links are plain links to the pad. They only show with a pad to point at.
    function quickView() {
      var store = window.Alpine && window.Alpine.store ? window.Alpine.store('xQuickView') : null;
      return store && store.enabled ? store : null;
    }

    function padQuickViewId() { return padEl.variant.id.replace(/^current-variant-/, ''); }

    function prefetchPadDetails(e) {
      var store = quickView();
      if (store) store.load(padPlan.pad.url, e.currentTarget, padQuickViewId());
    }

    function openPadDetails(e) {
      push('rug_pad_details_open', {
        pad_kind: padPlan.status, shape: padPlan.shape, product_id: config.productId, pad_product_id: padPlan.pad.id
      });
      var store = quickView();
      if (!store) return;
      e.preventDefault();
      store.load(padPlan.pad.url, e.currentTarget, padQuickViewId());
      watchQuickViewClose(store);
      padQuickViewOpen = true;
      store.open();
    }

    /* On close the theme refocuses only a product card's quick view button,
     * so focus fell to the body. Once the quick view THIS row opened closes,
     * focus goes back to the link; quick views opened anywhere else are left
     * alone. An add from inside it closes it and opens the cart: focus is the
     * cart's then. */
    var padQuickViewOpen = false;
    var watchingQuickView = false;
    function watchQuickViewClose(store) {
      if (watchingQuickView || !window.Alpine.effect) return;
      watchingQuickView = true;
      window.Alpine.effect(function () {
        if (store.show || !padQuickViewOpen) return;
        padQuickViewOpen = false;
        padEl.details.focus();
      });
      document.addEventListener('eurus:cart:items-changed', function () {
        if (!store.show) padQuickViewOpen = false;
      });
    }

    function fillPadNotice(node, message, plan) {
      node.appendChild(document.createTextNode(message));
      if (!plan) return;
      var link = document.createElement('a');
      link.href = padShopUrl(plan);
      link.className = 'underline';
      link.textContent = padCfg.text.failedLink;
      node.appendChild(document.createTextNode(' '));
      node.appendChild(link);
    }

    function showPadNotice(message, plan) {
      if (!padEl) return;
      padEl.notice.textContent = '';
      if (message) fillPadNotice(padEl.notice, message, plan);
    }

    // The rug made it, the pad did not: say so in the row (role=alert) and at
    // the top of the drawer's item list, where the next drawer re-render
    // replaces it. The box is unticked so the next add is the rug alone.
    function padFailed(plan) {
      padWanted = false;
      refreshPad();
      showPadNotice(padCfg.text.failed, plan);
      var items = document.getElementById('CartDrawer-CartItems');
      if (items) {
        var note = document.createElement('p');
        note.className = 'text-[rgba(var(--color-error))] text-normal mb-2';
        fillPadNotice(note, padCfg.text.failed, plan);
        items.insertBefore(note, items.firstChild);
      }
    }

    // The add button's own xProductCart component (product-cart.js), whose
    // `loading` flag is true exactly while that button's add is in flight.
    function themeButtonData(btn) {
      try {
        return window.Alpine && window.Alpine.$data ? window.Alpine.$data(btn) : null;
      } catch (e) { return null; }
    }

    // Drive the theme's own Alpine `loading` flag so the busy state looks
    // exactly like a normal add. The opacity is only the not-yet-booted
    // fallback.
    function setBusy(btn, on) {
      var data = themeButtonData(btn);
      if (data && typeof data.loading !== 'undefined') { data.loading = on; return; }
      var span = btn.querySelector('.x-atc-text');
      if (span) span.style.opacity = on ? '0.5' : '';
    }

    /* While an add is in flight the capture listener swallows every add click
     * in the section, so nothing a shopper does mid-add can send a second rug;
     * the pad box is disabled and the rug's add buttons are marked locked
     * (aria-disabled + the disabled look). Never through `disabled` on the
     * theme's buttons: the theme owns that one (a sold-out size disables
     * them), and the release must not switch them back on. */
    function setOrdering(btn, on) {
      ordering = on;
      if (btn !== el.add) setBusy(btn, on);
      rugButtons().forEach(function (b) {
        b.classList.toggle('cs-locked', on);
        if (on) b.setAttribute('aria-disabled', 'true');
        else b.removeAttribute('aria-disabled');
      });
      refreshPad();
    }

    // The lock is released however the add ends, a synchronous throw included:
    // otherwise every add button stays dead until a reload.
    function order(btn, run) {
      setOrdering(btn, true);
      var release = function () { setOrdering(btn, false); };
      var adding = null;
      try {
        adding = run();
      } finally {
        if (adding) {
          adding.then(release, function (err) {
            release();
            if (window.console) console.error('custom-size-rug: add failed', err);
          });
        } else release();
      }
    }

    function padVariant(plan, qty) {
      if (plan.status === 'listed') return Promise.resolve(plan.variant.id);
      // The endpoint caps quantity at 10, but it only shapes the checkout link
      // it returns; the cart line carries the real quantity.
      return request(Object.assign({ product_id: plan.pad.id, quantity: Math.min(qty, 10) }, plan.request))
        .then(function (payload) { return payload.variant_id; });
    }

    /* The rug and its pad are minted in parallel; past the wait budget
     * (waitForPad) the rug goes alone. ONE cart request with the pad as a
     * child line; if that is refused, the rug alone, once. Resolves with the
     * rug's variant id; rejects only when the RUG could not be added. */
    function addWithPad(rugReady, line, plan, attach) {
      return waitForPad(rugReady, plan ? padVariant(plan, line.quantity) : null, PAD_MIN_WAIT_MS, PAD_GRACE_MS)
        .then(function (got) {
          var rug = { id: got.rug, quantity: line.quantity, properties: line.properties, attributes: line.attributes };
          var pad = got.pad ? {
            id: got.pad, quantity: line.quantity, parent_id: got.rug,
            properties: padProperties(line.properties, padCfg.noteKey)
          } : null;
          return addRugWithPad(addToCart, rug, pad).then(function (result) {
            if (attach) {
              attach.pad_added = result.padAdded;
              push('rug_pad_attach', attach);
            }
            if (plan && !result.padAdded) padFailed(plan);
            return got.rug;
          });
        });
    }

    /* The listed rug exactly as the theme's own add would send it: the pressed
     * button's form (main or sticky), with its quantity, its non-empty
     * line-item properties (the delivery estimate) and its cart attributes. */
    function listedLine(btn) {
      var data = new FormData(btn.form);
      var store = window.Alpine && window.Alpine.store ? window.Alpine.store('xEstimateDelivery') : null;
      var line = { id: data.get('id'), quantity: parseInt(data.get('quantity'), 10) || 1, properties: {}, attributes: {} };
      data.forEach(function (value, key) {
        var m = /^(properties|attributes)\[(.+)\]$/.exec(key);
        if (!m || value === '' || m[2].indexOf('_linked_product_id') === 0) return;
        // product-cart.js fills the delivery estimate's cut-off in at add time.
        line[m[1]][m[2]] = store && store.noti && typeof value === 'string'
          ? value.replace('time_to_cut_off', store.noti) : value;
      });
      return line;
    }

    function addListed(btn, plan) {
      var line = listedLine(btn);
      var attach = padAttachEvent(plan, true, false, 'listed', config.productId);
      showPadNotice(null);
      // A listed rug has no panel to show an error in; the pad row is the
      // control the shopper just used.
      return addWithPad(Promise.resolve(line.id), line, plan, attach).catch(function (err) {
        showPadNotice((err && err.message) || config.errorText);
      });
    }

    function addCustom() {
      var d = dims();
      var plan = padForPanel();
      var attach = padEl ? padAttachEvent(padPlan, boxTicked(), false, 'custom', config.productId) : null;
      el.add.disabled = true;
      el.add.textContent = config.buttonBusyLabel;
      el.error.hidden = true;
      showPadNotice(null);
      track('add_to_cart', { shape: shape(), width: d.width, length: d.length });

      var rugReady = request({
        product_id: config.productId,
        shape: shape(),
        width_ft: parseInt(el.widthFt.value, 10),
        width_in: parseInt(el.widthIn.value, 10),
        length_ft: singleDimension() ? 0 : parseInt(el.lengthFt.value, 10),
        length_in: singleDimension() ? 0 : parseInt(el.lengthIn.value, 10),
        quantity: 1
      }).then(function (payload) { return payload.variant_id; });

      return addWithPad(rugReady, { quantity: 1, properties: {}, attributes: {} }, plan, attach)
        .then(function (variantId) {
          el.add.disabled = false;
          el.add.textContent = config.buttonAddedLabel;
          setTimeout(function () { el.add.textContent = panelLabel(); }, 2500);
          track('added', { variant_id: variantId });
        }, function (err) {
          fail(err && err.message);
        });
    }

    /* Counts every successful theme add (box unticked) with the row showing,
     * without touching its request: armed by the click, fired by
     * eurus:cart:items-changed, which 8.1's product-cart.js dispatches only on
     * success. That event is page-wide, so it counts only while the arming
     * button's own add is still `loading`: a refused or failed add never
     * fires it, and an unrelated later add cannot either. */
    function armNativeAttach(btn) {
      var event = btn ? padAttachEvent(listedPlan, boxTicked(), false, 'listed', config.productId) : null;
      nativeAttach = event ? { btn: btn, event: event } : null;
    }

    function nativeAddSucceeded() {
      var armed = nativeAttach;
      var data = armed && themeButtonData(armed.btn);
      if (!data || !data.loading) return;
      nativeAttach = null;
      push('rug_pad_attach', armed.event);
    }

    function swallow(e) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    }

    // Capture phase + stopImmediatePropagation: the theme binds its own add
    // handler to these buttons, so letting the event through would ALSO add
    // the rug alone. With the box unticked the theme's own add goes ahead
    // untouched. While an add of ours is in flight EVERY add click in the
    // section is swallowed (the panel's button included). A selected swatch
    // never gets here: swatch-order.js swallows the click on document first.
    scope.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('button[name="add"], .add_to_cart_button, [data-cs-add]');
      if (!btn || !scope.contains(btn)) return;
      if (ordering) { swallow(e); return; }
      if (!padEl || !isRugButton(btn)) return;
      var plan = padForButtons();
      armNativeAttach(plan ? null : btn);
      if (!plan) return;
      swallow(e);
      order(btn, function () { return addListed(btn, plan); });
    }, true);

    if (customSizeUsable) {
      /* Custom size and a standard size are alternatives, so choosing a standard
       * one closes the custom panel and drops its selected styling. Capture phase
       * because the theme stops propagation on some pickers. */
      document.addEventListener('change', function (e) {
        var t = e.target;
        if (!t || !t.name || !/^size(-\d+)?\s*$/i.test(t.name)) return;
        if (el.panel.hidden) return;
        setPanel(false);
      }, true);

      document.addEventListener('click', function (e) {
        if (!el.panel.hidden && isRealSizeOptionClick(e)) setPanel(false);
      }, true);

      document.addEventListener(SIZE_OPTION_EVENT, function (e) {
        if (e.detail && e.detail.source !== 'custom-size') setPanel(false);
      });

      injectSizeButton(root, config, togglePanel);

      [el.shape, el.widthFt, el.widthIn, el.lengthFt, el.lengthIn].forEach(function (input) {
        input && input.addEventListener('change', render);
      });

      el.add.addEventListener('click', function () {
        if (ordering) return;
        order(el.add, addCustom);
      });
    }

    // The theme swaps the variant picker and the add buttons' markup out on
    // every size change, taking the injected option and the "+ pad" labels
    // with them; put them back whenever that happens. aria-pressed is the
    // swatch option's selected state (swatch-order.js).
    var resync = null;
    new MutationObserver(function () {
      if (resync) return;
      resync = setTimeout(function () {
        resync = null;
        if (customSizeUsable) injectSizeButton(root, config, togglePanel);
        refreshPad();
      }, 0);
    }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-pressed'] });

    if (padEl) {
      document.addEventListener('eurus:cart:items-changed', nativeAddSucceeded);
      document.addEventListener(SIZE_OPTION_EVENT, refreshPad);
      [padEl.thumb, padEl.details].forEach(function (link) {
        link.addEventListener('mouseover', prefetchPadDetails);
        link.addEventListener('focus', prefetchPadDetails);
        link.addEventListener('click', openPadDetails);
      });
      padEl.input.addEventListener('change', function () {
        padWanted = padEl.input.checked;
        refreshPad();
      });
      // A variant change lands on the forms' id inputs. The quantity steppers
      // set the value through Alpine without an event, so re-read once their
      // click has settled.
      scope.addEventListener('change', function (e) {
        var name = e.target && e.target.name;
        if (name === 'id' || name === 'quantity') refreshPad();
      });
      scope.addEventListener('input', function (e) {
        if (e.target && e.target.name === 'quantity') refreshPad();
      });
      scope.addEventListener('click', function (e) {
        if (e.target.closest && e.target.closest('button[name="plus"], button[name="minus"]')) {
          setTimeout(refreshPad, 0);
        }
      });
    }

    if (customSizeUsable) render();
    else refreshPad();
  }

  function boot() {
    document.querySelectorAll('[data-custom-size-rug]').forEach(initWidget);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
